import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/roles';
import DashboardIcon from './icons/DashboardIcon';
import CandidateIcon from './icons/CandidateIcon';
import QueryIcon from './icons/QueryIcon';
import OpenRolesIcon from './icons/OpenRolesIcon';

const BRAND_BLUE = '#4841F0';

const TPReportsIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const Header = ({ activeTab, setActiveTab, onShowDataSources, showDataSources }) => {
  const { logout, username, user } = useAuth();
  const navigate = useNavigate();
  const canManageClients = user && (hasPermission(user, PERMISSIONS.MANAGE_CLIENT_ACCESS) || hasPermission(user, PERMISSIONS.MANAGE_USERS));
  return (
    <header style={{background: BRAND_BLUE}} className="shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center py-3 sm:py-4">
          {/* Logo */}
          <div className="flex items-center">
            <span style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 800,
              fontSize: '1.75rem',
              color: '#ffffff',
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}>
              Satellite
            </span>
          </div>

          {/* Navigation Tabs */}
          <nav className="flex items-center">
            <div className="flex rounded-lg p-1 space-x-1" style={{background: 'rgba(255,255,255,0.15)'}}>
              {[
                { key: 'overview', label: 'Overview', Icon: DashboardIcon },
                { key: 'candidates', label: 'Candidates', Icon: CandidateIcon },
                { key: 'open-roles', label: 'Open Roles', Icon: OpenRolesIcon },
                { key: 'query', label: 'AI Query', Icon: QueryIcon },
                ...(canManageClients ? [{ key: 'tp-reports', label: 'TP Reports', Icon: TPReportsIcon }] : []),
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className="px-2 sm:px-4 py-2 sm:py-2 rounded-md flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium transition-all duration-200"
                  style={{
                    background: activeTab === key ? '#ffffff' : 'transparent',
                    color: activeTab === key ? BRAND_BLUE : 'rgba(255,255,255,0.85)',
                  }}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>

            {/* Client Portal Admin */}
            {canManageClients && (
              <button
                onClick={() => navigate('/admin/clients')}
                className="ml-2 sm:ml-4 p-2 sm:p-2.5 rounded-lg transition-all duration-200"
                style={{
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.85)',
                }}
                title="Client Portal Access"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </button>
            )}

            {/* Settings Button */}
            {onShowDataSources && (
              <button
                onClick={onShowDataSources}
                className="ml-2 sm:ml-4 p-2 sm:p-2.5 rounded-lg transition-all duration-200"
                style={{
                  background: showDataSources ? 'rgba(255,255,255,0.25)' : 'transparent',
                  color: 'rgba(255,255,255,0.85)',
                }}
                title="Data Sources"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            )}

            {/* User Menu */}
            <div className="ml-2 sm:ml-4 flex items-center space-x-2 sm:space-x-3">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium" style={{color: '#ffffff'}}>{username}</p>
              </div>
              <button
                onClick={logout}
                className="p-2 sm:p-2.5 rounded-lg transition-all duration-200"
                style={{color: 'rgba(255,255,255,0.75)'}}
                title="Logout"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
